import { createApi } from '@reduxjs/toolkit/query/react';
import { createEntityAdapter } from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import { EVENT_SIZE } from '../constants/AppConstants';
import { axiosBaseQuery } from '../helpers/AxiosHelper';

// Parse a timestamp value into UTC epoch ms, robust to the formats this
// store actually ingests: ISO with `Z` (V3 REST), MySQL-style
// "YYYY-MM-DD HH:mm:ss" with no tz (some WebSocket payloads), already-a-Date,
// or numeric epoch. moment.utc() interprets a tz-less string as UTC instead
// of local — which is what we want, since the server stores timestamps in
// UTC. Naive `new Date(str)` interprets tz-less strings as local time and
// would otherwise push WS-pushed events to the wrong slot in the thread,
// only to "fix" on refresh once the V3 REST endpoint returns canonical ISO.
const toEpochMs = (t) => {
  if (t == null) return Number.NEGATIVE_INFINITY;
  if (typeof t === 'number') return t;
  const m = moment.utc(t);
  return m.isValid() ? m.valueOf() : Number.NEGATIVE_INFINITY;
};

// Create Adapter For Thread To Avoid Duplicates
const threadAdapter = createEntityAdapter({
  sortComparer: (a, b) => toEpochMs(a.timestamp) - toEpochMs(b.timestamp),
  selectId: (event) => event.id,
});
const threadSelectors = threadAdapter.getSelectors((state) => state);

const shapeThreadResult = (result) => result?.data?.Events || [];

// Define our single API slice object
export const threadSlice = createApi({
  reducerPath: 'threadV3',
  baseQuery: axiosBaseQuery({
    baseURL: process.env.REACT_APP_V3_BASE_URL,
  }),
  tagTypes: ['Thread'],
  endpoints: (builder) => ({
    getThread: builder.query({
      query: (options) => {
        const {
          userId,
          groupId,
          following = false,
          direct = false,
          organizationId,
          assigned = false,
          pageNo = 0,
          channelIds = [],
        } = options;
        if (!userId) return null;
        let url = `/organizations/${organizationId}/users/${userId}/thread?page=${pageNo}&pageSize=${EVENT_SIZE}&following=${following}&direct=${direct}&assigned=${assigned}&sort=Descending`;
        if (channelIds.length > 0) {
          url = `${url}&channelIds=${channelIds}`;
        } else if (groupId) {
          url = `${url}&groupId=${groupId}`;
        }
        return { url, method: 'get', shapeResult: shapeThreadResult };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        // Omit pageNo param from cache key
        const {
          pageNo,
          ...otherQueryArgs
        } = queryArgs;
        return otherQueryArgs;
      },
      providesTags: (result, error, arg) =>
        [{ type: 'Thread', id: arg.userId }, 'Thread'],
      transformResponse: (response) =>
        threadAdapter.addMany(
          threadAdapter.getInitialState({
            hasMorePages: response.length === EVENT_SIZE,
          }),
          response,
        ),
      merge(currentCacheData, responseData) {
        threadAdapter.upsertMany(
          currentCacheData,
          threadSelectors.selectAll(responseData),
        );
        currentCacheData.hasMorePages = responseData.hasMorePages; // eslint-disable-line no-param-reassign
      },
    }),
  }),
});
export function addEventToQueryResults(payload, threadOptions) {
  return (dispatch) => {
    dispatch(
      threadSlice.util.updateQueryData('getThread', threadOptions, (draft) => {
        threadAdapter.addOne(
          draft,
          payload,
        );
      }),
    );
  };
}

export function upsertEventInQueryResults(payload, threadOptions) {
  return (dispatch) => {
    dispatch(
      threadSlice.util.updateQueryData('getThread', threadOptions, (draft) => {
        threadAdapter.upsertOne(
          draft,
          payload,
        );
      }),
    );
  };
}

// replaces optimistic id with event id
export function updateEventInQueryResults({ eventId, payload, threadOptions }) {
  return (dispatch) => {
    dispatch(
      threadSlice.util.updateQueryData('getThread', threadOptions, (draft) => {
        const event = threadSelectors.selectById(draft, eventId);
        if (event) {
          threadAdapter.updateOne(
            draft,
            { id: eventId, changes: payload },
          );
        } else {
          threadAdapter.upsertOne(
            draft,
            payload,
          );
        }
      }),
    );
  };
}

export { threadSelectors, threadAdapter };

export const { useGetThreadQuery, useLazyGetThreadQuery, useGetThreadQueryState } = threadSlice;

export default threadSlice;
